import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowUpDown, ArrowUp, ArrowDown, Eye, Star, Trash2, FileDown, Gavel, Lock,
  Phone, ExternalLink, MapPin,
} from "lucide-react";

const CCY_SYMBOLS = { EUR: "€", USD: "$", CAD: "CA$", AUD: "A$", AED: "AED", TRY: "₺", GBP: "£", CHF: "CHF" };
const CCY_PREFIX = { USD: true, CAD: true, AUD: true, GBP: true };

// Seuils cohérents avec la carte (RadarMap BAND_COLOR) : vert ≥70, orange 40-69, rouge <40
export const scoreBand = (s) => (s >= 70 ? "green" : s >= 40 ? "orange" : "red");
const BAND_HEX = { green: "#16a34a", orange: "#f59e0b", red: "#dc2626" };

function money(eur, ccy, rates, suffix = "") {
  if (eur === null || eur === undefined || eur === "") return "—";
  const rate = ccy === "EUR" ? 1 : rates?.[ccy] || null;
  if (!rate) return "—";
  const v = Math.round(Number(eur) * rate);
  const sym = CCY_SYMBOLS[ccy] || ccy;
  const num = v.toLocaleString("fr-FR");
  const base = CCY_PREFIX[ccy] ? `${sym}${num}` : `${num} ${sym}`;
  return suffix ? `${base}${suffix}` : base;
}

const pm2Of = (l) => (l.surface && l.current_price ? Math.round(l.current_price / l.surface) : null);
const priceOf = (l) => (l.kind === "auction" ? l.mise_a_prix : l.current_price) || 0;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
  catch { return "—"; }
};

export default function RadarTable({ listings, ccy = "EUR", rates = {}, onOpen, onRemove, onTrack, onExport, canExport, navigate, selectedId, onHover, selectable = false, selectedIds, onToggleSelect, onToggleAll }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ key: "score", dir: "desc" });
  const selSet = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const isChecked = (id) => selSet.has(id);

  const bandLabel = (b) => (b === "green" ? t("radar.table.toContact") : b === "orange" ? t("radar.table.toStudy") : t("radar.table.lowPot"));
  const followLabel = { saved: t("radar.table.followSaved"), contacted: t("radar.table.followContacted"), following: t("radar.table.followFollowing") };

  const sorted = useMemo(() => {
    const arr = [...listings];
    const getVal = (l) => {
      switch (sort.key) {
        case "price": return priceOf(l);
        case "pm2": return pm2Of(l) || 0;
        case "surface": return l.surface || 0;
        case "city": return (l.city || "").toLowerCase();
        case "published_at": return new Date(l.published_at || 0).getTime();
        case "first_seen": return new Date(l.first_seen || 0).getTime();
        default: return l.score || 0;
      }
    };
    arr.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [listings, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  const SortIcon = ({ k }) => sort.key !== k ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;

  const Th = ({ k, label, sortable = true, className = "" }) => (
    <th className={`whitespace-nowrap px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${className}`}>
      {sortable ? (
        <button onClick={() => toggleSort(k)} data-testid={`radar-th-${k}`} className="inline-flex items-center gap-1 transition-colors hover:text-[#0F172A]">{label} <SortIcon k={k} /></button>
      ) : label}
    </th>
  );

  const FollowBadge = ({ l }) => {
    const fs = l.follow_status && l.follow_status !== "none" ? l.follow_status : null;
    if (!fs) return <span className="text-xs text-slate-300">—</span>;
    const color = fs === "contacted" ? "bg-emerald-100 text-emerald-800" : fs === "following" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700";
    return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${color}`}>{followLabel[fs]}</span>;
  };

  const Actions = ({ l }) => (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      <button title={t("radar.table.open")} data-testid={`radar-row-open-${l.id}`} onClick={() => (l.locked ? navigate("/abonnement") : onOpen(l.id))} className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#0F172A]"><Eye className="h-4 w-4" /></button>
      {!l.locked && (
        <>
          <button title={t("radar.table.favorite")} data-testid={`radar-row-fav-${l.id}`} onClick={() => onTrack(l.id, { favorite: !l.favorite })} className="rounded-md p-1 transition-colors hover:bg-slate-100" style={{ color: l.favorite ? "#F5A623" : "#94a3b8" }}><Star className="h-4 w-4" fill={l.favorite ? "#F5A623" : "none"} /></button>
          <button title={t("radar.table.markContacted")} data-testid={`radar-row-contacted-${l.id}`} onClick={() => onTrack(l.id, { follow_status: l.follow_status === "contacted" ? "none" : "contacted" })} className="rounded-md p-1 transition-colors hover:bg-slate-100" style={{ color: l.follow_status === "contacted" ? "#16a34a" : "#94a3b8" }}><Phone className="h-4 w-4" /></button>
          {canExport && <button title={t("radar.table.exportPdf")} data-testid={`radar-row-export-${l.id}`} onClick={() => onExport(l.id)} className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#0F172A]"><FileDown className="h-4 w-4" /></button>}
          <button title={t("radar.table.remove")} data-testid={`radar-row-del-${l.id}`} onClick={() => onRemove(l.id)} className="rounded-md p-1 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </>
      )}
      {l.locked && <button data-testid={`radar-row-unlock-${l.id}`} onClick={() => navigate("/abonnement")} className="text-[11px] font-semibold text-[#C6A87C] hover:underline">{t("radar.table.unlock")}</button>}
    </div>
  );

  const ScoreBadge = ({ l }) => {
    const b = scoreBand(l.score);
    return <span className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[12px] font-bold text-white" style={{ background: BAND_HEX[b] }}>{l.score}</span>;
  };

  return (
    <div data-testid="radar-table">
      {/* ---- Table (tablette + desktop) ---- */}
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white md:block">
        <table className="w-full min-w-[880px] border-collapse text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {selectable && (
                <th className="w-9 px-2.5 py-2 text-center">
                  <input type="checkbox" data-testid="radar-select-all-header"
                    aria-label={t("radar.results.selectAll", "Tout sélectionner")}
                    checked={sorted.length > 0 && sorted.every((l) => isChecked(l.id))}
                    onChange={(e) => onToggleAll && onToggleAll(sorted.map((l) => l.id), e.target.checked)}
                    className="h-4 w-4 accent-[#C6A87C]" />
                </th>
              )}
              <Th k="score" label={t("radar.table.score")} />
              <Th k="status" label={t("radar.table.statusCol")} sortable={false} />
              <Th k="type" label={t("radar.table.type")} sortable={false} />
              <Th k="city" label={t("radar.table.city")} />
              <Th k="surface" label={t("radar.table.surface")} className="text-right" />
              <Th k="price" label={t("radar.table.asking")} className="text-right" />
              <Th k="pm2" label={t("radar.table.pm2")} className="text-right" />
              <Th k="published_at" label={t("radar.table.published")} />
              <Th k="first_seen" label={t("radar.table.detected")} />
              <Th k="source" label={t("radar.table.source")} sortable={false} />
              <Th k="follow" label={t("radar.table.follow")} sortable={false} />
              <th className="px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("radar.table.action")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const b = scoreBand(l.score);
              const isSel = selectedId === l.id;
              const lk = l.locked ? "blur-[6px] select-none" : "";
              return (
                <tr key={l.id} data-testid={`radar-row-${l.id}`}
                  onMouseEnter={() => onHover && onHover(l.id)} onMouseLeave={() => onHover && onHover(null)}
                  onClick={() => (l.locked ? navigate("/abonnement") : onOpen(l.id))}
                  className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-[#C6A87C]/[0.07] ${isSel ? "bg-[#C6A87C]/10" : ""}`}
                  style={{ borderLeft: `3px solid ${BAND_HEX[b]}` }}>
                  {selectable && (
                    <td className="px-2.5 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`radar-select-${l.id}`}
                        checked={isChecked(l.id)} onChange={() => onToggleSelect && onToggleSelect(l.id)}
                        className="h-4 w-4 accent-[#C6A87C]" />
                    </td>
                  )}
                  <td className="px-2.5 py-2"><ScoreBadge l={l} /></td>
                  <td className="whitespace-nowrap px-2.5 py-2 font-semibold" style={{ color: BAND_HEX[b] }}>
                    <span className="inline-flex items-center gap-1.5">
                      {l.kind === "auction" && <Gavel className="h-3.5 w-3.5 text-[#7c2d12]" />}
                      {bandLabel(b)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-slate-700"><span className={lk}>{l.property_type || "—"}</span></td>
                  <td className="px-2.5 py-2 text-slate-700">
                    <span className={`block max-w-[120px] truncate ${lk}`}>{l.city || "—"}</span>
                    {l.quartier && <span className={`block max-w-[120px] truncate text-xs text-slate-400 ${lk}`}>{l.quartier}</span>}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-right text-slate-600"><span className={lk}>{l.surface ? `${Math.round(l.surface)} m²` : "—"}</span></td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-right font-semibold text-[#0F172A]" data-testid={`radar-row-price-${l.id}`}><span className={lk}>{money(priceOf(l), ccy, rates)}</span></td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-right text-slate-600"><span className={lk}>{pm2Of(l) ? money(pm2Of(l), ccy, rates, "/m²") : "—"}</span></td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-slate-500"><span className={lk}>{fmtDate(l.published_at)}</span></td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-slate-500"><span className={lk}>{fmtDate(l.first_seen)}</span></td>
                  <td className="px-2.5 py-2">
                    {l.locked
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[#C6A87C]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a6d3b]"><Lock className="h-2.5 w-2.5" /> {t("radar.table.locked", "Verrouillé")}</span>
                      : l.source_verified === false || !(l.url && String(l.url).startsWith("http") && l.source_host)
                      ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">{t("radar.table.unverified")}</span>
                      : <span className="block max-w-[86px] truncate text-xs text-slate-500">{l.source_host || "—"}</span>}
                  </td>
                  <td className="px-2.5 py-2"><FollowBadge l={l} /></td>
                  <td className="px-2.5 py-2"><Actions l={l} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Cartes empilées (mobile) ---- */}
      <div className="grid gap-3 md:hidden">
        {sorted.map((l) => {
          const b = scoreBand(l.score);
          const lk = l.locked ? "blur-[6px] select-none" : "";
          return (
            <motion.div key={l.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`radar-mcard-${l.id}`} onClick={() => (l.locked ? navigate("/abonnement") : onOpen(l.id))}
              className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white" style={{ borderLeft: `4px solid ${BAND_HEX[b]}` }}>
              <div className="flex items-start gap-3 p-3.5">
                {selectable && (
                  <input type="checkbox" data-testid={`radar-select-m-${l.id}`}
                    checked={isChecked(l.id)} onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleSelect && onToggleSelect(l.id)}
                    className="mt-1 h-4 w-4 accent-[#C6A87C]" />
                )}
                <ScoreBadge l={l} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold" style={{ color: BAND_HEX[b] }}>{bandLabel(b)}</span>
                    {l.kind === "auction" && <span className="inline-flex items-center gap-1 rounded bg-[#7c2d12] px-1.5 py-0.5 text-[9px] font-bold text-white"><Gavel className="h-2.5 w-2.5" /> {t("radar.table.auction")}</span>}
                    {l.locked && <Lock className="h-3.5 w-3.5 text-[#C6A87C]" />}
                  </div>
                  <p className={`mt-0.5 truncate text-sm text-slate-700 ${lk}`}>{l.property_type || "—"} · {l.city || "—"}{l.quartier ? ` · ${l.quartier}` : ""}</p>
                  <div className={`mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs ${lk}`}>
                    <span className="font-semibold text-[#0F172A]">{money(priceOf(l), ccy, rates)}</span>
                    {l.surface ? <span className="text-slate-500">{Math.round(l.surface)} m²</span> : null}
                    {pm2Of(l) ? <span className="text-slate-500">{money(pm2Of(l), ccy, rates, "/m²")}</span> : null}
                  </div>
                  <div className={`mt-1 flex items-center gap-2 text-[11px] text-slate-400 ${lk}`}>
                    <MapPin className="h-3 w-3" /> {l.source_host || t("radar.table.manualSource")} · {fmtDate(l.first_seen)}
                  </div>
                  {l.locked && <p className="mt-1.5 text-[11px] font-semibold text-[#C6A87C]">{t("radar.table.lockedHint", "Abonnez-vous pour débloquer cette opportunité")}</p>}
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 px-2 py-1.5">
                <FollowBadge l={l} />
                <Actions l={l} />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
